import { useState, useEffect } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';

interface Task {
  id: string;
  text: string;
  done: boolean;
  category: 'routine' | 'todo';
  priority: 'high' | 'medium' | 'low';
  createdAt: string;
  dueDate?: string;
}

const STORAGE_KEY = 'ochrepoint_admin_tasks';

const DEFAULT_ROUTINE: Task[] = [
  { id: 'r1', text: 'Review and approve pending timesheets', done: false, category: 'routine', priority: 'high', createdAt: new Date().toISOString() },
  { id: 'r2', text: 'Check overdue invoices and follow up with clients', done: false, category: 'routine', priority: 'high', createdAt: new Date().toISOString() },
  { id: 'r3', text: 'Review upcoming scheduled jobs for the week', done: false, category: 'routine', priority: 'high', createdAt: new Date().toISOString() },
  { id: 'r4', text: 'Check dashboard alerts and resolve any issues', done: false, category: 'routine', priority: 'medium', createdAt: new Date().toISOString() },
  { id: 'r5', text: 'Follow up on outstanding quotes', done: false, category: 'routine', priority: 'medium', createdAt: new Date().toISOString() },
  { id: 'r6', text: 'Update job statuses in the system', done: false, category: 'routine', priority: 'medium', createdAt: new Date().toISOString() },
  { id: 'r7', text: 'Check materials inventory for upcoming jobs', done: false, category: 'routine', priority: 'low', createdAt: new Date().toISOString() },
  { id: 'r8', text: 'Confirm crew availability for next week', done: false, category: 'routine', priority: 'low', createdAt: new Date().toISOString() },
];

const PRIORITY_CONFIG = {
  high: { label: 'High', color: 'text-red-600 bg-red-50 border-red-200' },
  medium: { label: 'Medium', color: 'text-amber-600 bg-amber-50 border-amber-200' },
  low: { label: 'Low', color: 'text-green-600 bg-green-50 border-green-200' },
};

function loadTasks(): Task[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : DEFAULT_ROUTINE;
  } catch {
    return DEFAULT_ROUTINE;
  }
}

function saveTasks(tasks: Task[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(tasks));
}

export const AdminTasksPage = () => {
  const [tasks, setTasks] = useState<Task[]>(loadTasks);
  const [newText, setNewText] = useState('');
  const [newPriority, setNewPriority] = useState<'high' | 'medium' | 'low'>('medium');
  const [newDue, setNewDue] = useState('');
  const [activeTab, setActiveTab] = useState<'routine' | 'todo'>('routine');
  const [showAddForm, setShowAddForm] = useState(false);

  useEffect(() => { saveTasks(tasks); }, [tasks]);

  const routineTasks = tasks.filter(t => t.category === 'routine');
  const todoTasks = tasks.filter(t => t.category === 'todo');
  const currentTasks = activeTab === 'routine' ? routineTasks : todoTasks;
  const pending = currentTasks.filter(t => !t.done);
  const done = currentTasks.filter(t => t.done);

  const toggle = (id: string) => {
    setTasks(ts => ts.map(t => t.id === id ? { ...t, done: !t.done } : t));
  };

  const addTask = () => {
    if (!newText.trim()) return;
    const task: Task = {
      id: crypto.randomUUID(),
      text: newText.trim(),
      done: false,
      category: activeTab,
      priority: newPriority,
      createdAt: new Date().toISOString(),
      dueDate: newDue || undefined,
    };
    setTasks(ts => [task, ...ts]);
    setNewText('');
    setNewDue('');
    setShowAddForm(false);
  };

  const removeTask = (id: string) => {
    setTasks(ts => ts.filter(t => t.id !== id));
  };

  const resetRoutine = () => {
    setTasks(ts => ts.map(t => t.category === 'routine' ? { ...t, done: false } : t));
  };

  const clearDone = () => {
    setTasks(ts => ts.filter(t => !(t.category === activeTab && t.done)));
  };

  const completedCount = currentTasks.filter(t => t.done).length;
  const totalCount = currentTasks.length;
  const progressPct = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  return (
    <MainLayout>
      <div className="flex flex-col min-h-screen">
        <header className="sticky top-14 md:top-0 z-20 border-b border-gray-200 px-4 py-4 md:px-8 md:py-5 bg-gray-50/80 backdrop-blur-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-4">
              <div className="bg-primary/10 rounded-md p-2">
                <span className="material-symbols-outlined text-primary text-2xl">task_alt</span>
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">My Tasks</h2>
                <p className="text-sm text-gray-500">Daily routines and to-do items</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {done.length > 0 && (
                <button
                  onClick={clearDone}
                  className="px-3 py-2 text-sm text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  Clear completed
                </button>
              )}
              {activeTab === 'routine' && completedCount > 0 && (
                <button
                  onClick={resetRoutine}
                  className="px-3 py-2 text-sm text-primary border border-primary/30 rounded-lg hover:bg-primary/5"
                >
                  Reset routine
                </button>
              )}
              <button
                onClick={() => setShowAddForm(v => !v)}
                className="flex items-center gap-1.5 px-4 py-2 bg-primary text-white text-sm font-semibold rounded-lg hover:opacity-90"
              >
                <span className="material-symbols-outlined text-base">add</span>
                Add Task
              </button>
            </div>
          </div>
        </header>

        <div className="p-4 md:p-8">
          <div className="max-w-2xl mx-auto">
            {/* Tabs */}
            <div className="flex gap-1 mb-5 p-1 bg-gray-100 rounded-xl w-fit">
              {(['routine', 'todo'] as const).map(tab => (
                <button
                  key={tab}
                  onClick={() => { setActiveTab(tab); setShowAddForm(false); }}
                  className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all ${
                    activeTab === tab ? 'bg-white text-primary shadow-sm' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab === 'routine' ? 'Daily Routine' : 'To-Do List'}
                  <span className={`ml-2 text-xs px-1.5 py-0.5 rounded-full ${
                    activeTab === tab ? 'bg-primary/10 text-primary' : 'bg-gray-200 text-gray-500'
                  }`}>
                    {(tab === 'routine' ? routineTasks : todoTasks).filter(t => !t.done).length}
                  </span>
                </button>
              ))}
            </div>

            {/* Progress bar */}
            {totalCount > 0 && (
              <div className="mb-5">
                <div className="flex justify-between text-xs text-gray-500 mb-1">
                  <span>{completedCount} of {totalCount} complete</span>
                  <span>{progressPct}%</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2">
                  <div
                    className="bg-primary rounded-full h-2 transition-all duration-500"
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
              </div>
            )}

            {/* Add form */}
            {showAddForm && (
              <div className="mb-5 bg-white rounded-xl border border-gray-200 p-4 space-y-3">
                <h4 className="text-sm font-semibold text-gray-900">
                  Add {activeTab === 'routine' ? 'Routine Task' : 'To-Do Item'}
                </h4>
                <input
                  type="text"
                  placeholder="What needs to be done?"
                  value={newText}
                  onChange={e => setNewText(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && addTask()}
                  autoFocus
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
                <div className="flex gap-3">
                  <div className="flex-1">
                    <label className="block text-xs text-gray-500 mb-1">Priority</label>
                    <select
                      value={newPriority}
                      onChange={e => setNewPriority(e.target.value as 'high' | 'medium' | 'low')}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                    >
                      <option value="high">High</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                    </select>
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs text-gray-500 mb-1">Due Date (optional)</label>
                    <input
                      type="date"
                      value={newDue}
                      onChange={e => setNewDue(e.target.value)}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                    />
                  </div>
                </div>
                <div className="flex gap-2 pt-1">
                  <button onClick={addTask} className="px-4 py-2 bg-primary text-white text-sm font-semibold rounded-lg hover:opacity-90">
                    Add
                  </button>
                  <button onClick={() => setShowAddForm(false)} className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Pending tasks */}
            {pending.length > 0 && (
              <div className="space-y-2 mb-4">
                {pending.map(task => (
                  <TaskRow key={task.id} task={task} onToggle={toggle} onRemove={removeTask} />
                ))}
              </div>
            )}

            {pending.length === 0 && done.length === 0 && (
              <div className="text-center py-16 text-gray-400">
                <span className="material-symbols-outlined text-5xl mb-3 block">task_alt</span>
                <p className="text-sm">No tasks yet. Add one above.</p>
              </div>
            )}

            {pending.length === 0 && done.length > 0 && (
              <div className="text-center py-8 text-gray-400">
                <span className="material-symbols-outlined text-4xl mb-2 block text-green-400">check_circle</span>
                <p className="text-sm text-green-600 font-medium">All done! Great work.</p>
              </div>
            )}

            {/* Completed tasks */}
            {done.length > 0 && (
              <div className="mt-6">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Completed ({done.length})</p>
                <div className="space-y-2 opacity-60">
                  {done.map(task => (
                    <TaskRow key={task.id} task={task} onToggle={toggle} onRemove={removeTask} />
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'routine' && (
              <div className="mt-8 p-4 bg-blue-50 border border-blue-100 rounded-xl text-sm text-blue-700">
                <div className="flex items-start gap-2">
                  <span className="material-symbols-outlined text-base mt-0.5">info</span>
                  <div>
                    <strong>Daily Routine</strong> — These are recurring tasks to keep the business running smoothly. Click "Reset routine" each morning to start fresh.
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

function TaskRow({ task, onToggle, onRemove }: { task: Task; onToggle: (id: string) => void; onRemove: (id: string) => void }) {
  const pc = PRIORITY_CONFIG[task.priority];
  const isOverdue = task.dueDate && !task.done && new Date(task.dueDate) < new Date();
  return (
    <div className={`flex items-center gap-3 bg-white rounded-xl border px-4 py-3 transition-all ${task.done ? 'border-gray-100' : 'border-gray-200'}`}>
      <button
        onClick={() => onToggle(task.id)}
        className={`flex-shrink-0 size-5 rounded-full border-2 flex items-center justify-center transition-colors ${
          task.done ? 'border-primary bg-primary' : 'border-gray-300 hover:border-primary'
        }`}
      >
        {task.done && <span className="material-symbols-outlined text-white text-xs">check</span>}
      </button>
      <span className={`flex-1 text-sm ${task.done ? 'line-through text-gray-400' : 'text-gray-800'}`}>
        {task.text}
      </span>
      {!task.done && (
        <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${pc.color}`}>{pc.label}</span>
      )}
      {task.dueDate && !task.done && (
        <span className={`text-xs ${isOverdue ? 'text-red-500 font-medium' : 'text-gray-400'}`}>
          {isOverdue ? 'Overdue · ' : ''}{new Date(task.dueDate).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })}
        </span>
      )}
      <button
        onClick={() => onRemove(task.id)}
        className="text-gray-300 hover:text-red-400 transition-colors flex-shrink-0"
        title="Remove"
      >
        <span className="material-symbols-outlined text-base">close</span>
      </button>
    </div>
  );
}
